import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AffiliateSummary, useAffiliates } from '../../queries/affiliates';
import { AffiliateFormDialog } from './AffiliateFormDialog';
import { PageHeader } from '../../components/ui/PageHeader';
import { StatCard } from '../../components/ui/StatCard';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable';

const money = (v: string | number) => `$${Number(v).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const rate = (a: AffiliateSummary) => (a.commission_type === 'percentage' ? `${Number(a.commission_value)}%` : money(a.commission_value));

export function AffiliatesPage() {
  const { data: affiliates = [], isLoading } = useAffiliates();
  const [showCreate, setShowCreate] = useState(false);

  const totalPaid = affiliates.reduce((sum, a) => sum + Number(a.commission_paid), 0);
  const totalFailed = affiliates.reduce((sum, a) => sum + Number(a.commission_failed), 0);

  const columns: DataTableColumn<AffiliateSummary>[] = [
    {
      key: 'affiliate',
      header: 'Affiliate',
      priority: true,
      render: (a) => (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <Link to={`/affiliates/${a.id}`} className="font-medium text-primary underline-offset-4 hover:underline">
            {a.display_name || a.affiliate_code}
          </Link>
          {a.affiliate_level > 1 && <Badge variant="outline">L{a.affiliate_level}</Badge>}
          {!a.stripe_account_id && <Badge variant="warning">no Stripe</Badge>}
        </span>
      ),
    },
    { key: 'status', header: 'Status', priority: true, render: (a) => (a.status === 'active' ? <Badge variant="success">active</Badge> : <Badge variant="outline">{a.status}</Badge>) },
    { key: 'rate', header: 'Rate', priority: false, render: (a) => <span className="text-muted-foreground @2xl:text-foreground">{rate(a)}</span> },
    { key: 'referrals', header: 'Referrals', priority: false, alignEnd: true, render: (a) => <span className="tabular-nums text-muted-foreground @2xl:text-foreground">{a.referral_count}</span> },
    { key: 'converted', header: 'Converted', priority: false, alignEnd: true, render: (a) => <span className="tabular-nums text-muted-foreground @2xl:text-foreground">{a.converted_count}</span> },
    { key: 'paid', header: 'Paid', priority: true, alignEnd: true, render: (a) => <span className="font-medium tabular-nums">{money(a.commission_paid)}</span> },
    {
      key: 'failed',
      header: 'Failed',
      priority: false,
      alignEnd: true,
      render: (a) => <span className={`tabular-nums ${Number(a.commission_failed) > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>{money(a.commission_failed)}</span>,
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Affiliates"
        description="Referral partners and their commissions. Figures come from the live commission ledger."
        actions={
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4" />
            New affiliate
          </Button>
        }
      />
      {showCreate && <AffiliateFormDialog onClose={() => setShowCreate(false)} />}

      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard label="Affiliates" value={affiliates.length} />
            <StatCard label="Active" value={affiliates.filter((a) => a.status === 'active').length} />
            <StatCard label="Commissions paid" value={money(totalPaid)} />
            <StatCard label="Failed payouts" value={money(totalFailed)} hint={totalFailed > 0 ? 'Needs attention' : undefined} trend={totalFailed > 0 ? 'down' : 'neutral'} />
          </div>

          <DataTable columns={columns} data={affiliates} getRowKey={(a) => String(a.id)} emptyMessage="No affiliates." />
        </>
      )}
    </div>
  );
}
